import React, { FC, FormEvent } from "react"

import { yupResolver } from "@hookform/resolvers/yup"
import { Box, Button, Divider, Grid, Stack, Typography } from "@mui/material"
import { useMutationCreateTeacher } from "api/reactQuery/mutations/adminTeacher"
import { CreateTeacherInput } from "api/reactQuery/mutations/adminTeacher.types"
import TextField from "components/form/common/field/textField"
import { useIsBreakpointUp } from "hooks/breakpoint"
import { useCustomSnackbar } from "hooks/snackbar"
import { useForm, FormProvider } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { RoutePath } from "routes/Route.types"
import { getErrorMessage } from "utils/api"

import { defaultValues, validateSchema } from "./CreateTeacher.config"

const CreateTeacher: FC = () => {
  const { t } = useTranslation()
  const { showSnackbar } = useCustomSnackbar()
  const navigate = useNavigate()
  const isTablet = useIsBreakpointUp("tablet")
  const methods = useForm<CreateTeacherInput>({
    resolver: yupResolver(validateSchema),
    defaultValues: defaultValues,
  })

  const handleGoBack = () => {
    navigate(RoutePath.ADMIN_TEACHERS)
  }

  const { mutate: createTeacher, isLoading } = useMutationCreateTeacher({
    options: {
      onSuccess: () => {
        methods.reset(defaultValues)
        showSnackbar({
          title: t("adminDashboard.teacherCreated"),
        })
      },
      onError: (err) => {
        showSnackbar({
          title: getErrorMessage(err),
          variant: "error",
        })
      },
    }
  })

  const onSubmit = (data: CreateTeacherInput) => {
    createTeacher(data)
  }

  return (
    <Box
      component="form"
      onSubmit={(e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        methods.handleSubmit(onSubmit)()
      }}
      sx={{
        maxWidth: 500,
        width: "100%",
      }}
    >
      <FormProvider {...methods}>
        <Stack spacing={2.5}>
          <Typography
            variant="body2"
            color="text.secondary"
          >
            {t("adminDashboard.createTeacherDescription")}
          </Typography>

          <Divider />

          <Grid container spacing={3}>
            <Grid size={{ desktop: 6, tablet: 6, mobile: 12 }}>
              <TextField
                name="first_name"
                type="text"
                label={t("teacherSettings.firstName")}
                fullWidth
              />
            </Grid>
            <Grid size={{ desktop: 6, tablet: 6, mobile: 12 }}>
              <TextField
                name="last_name"
                type="text"
                label={t("teacherSettings.lastName")}
                fullWidth
              />
            </Grid>
            <Grid size={{ desktop: 12, tablet: 12, mobile: 12 }}>
              <TextField
                name="email"
                type="email"
                label={t("teacherSettings.email")}
                fullWidth
              />
            </Grid>
          </Grid>

          <Box sx={{ pt: 2 }}>
            <Stack 
              flexDirection={isTablet ? "row" : "column"} 
              gap={2}
            >
              <Button
                onClick={handleGoBack}
                variant="outlined"
                size="large"
                fullWidth={!isTablet}
                sx={isTablet ? { flex: 1 } : {}}
              >
                {t("adminDashboard.backToTeachers")}
              </Button>
              <Button 
                type="submit" 
                size="large"
                loading={isLoading}
                fullWidth={!isTablet}
                sx={isTablet ? { flex: 1 } : {}}
              >
                {t("adminDashboard.addTeacher")}
              </Button>
            </Stack>
          </Box>
        </Stack>
      </FormProvider>
    </Box>
  )
}

export default CreateTeacher
